import AndroidIcon from "@mui/icons-material/Android";
import AppleIcon from "@mui/icons-material/Apple";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DevicesIcon from "@mui/icons-material/Devices";
import ErrorIcon from "@mui/icons-material/Error";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { useCallback, useContext, useMemo } from "react";
import { useFragment, useMutation } from "react-relay";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import { InternPushStatusCardTestMutation } from "./__generated__/InternPushStatusCardTestMutation.graphql";
import { InternPushStatusCard_status$key } from "./__generated__/InternPushStatusCard_status.graphql";
import { useColorMode } from "../../../contexts/ColorModeContext";
import SnackbarContext from "../../../contexts/SnackbarContext";
import formatNumber from "../../../utils/formatNumber";

const statusFragment = graphql`
  fragment InternPushStatusCard_status on PlatformPushNotificationStatus {
    azureApiConnected
    azureApiError
    notificationHubConnected
    notificationHubError
    notificationsSent30d
    pushDeliveries30d
    pushTaps30d
    tapRate
    totalRegisteredDevices
    iosDevices
    androidDevices
    totalActiveBrands
    brandsWithHub
    brandsWithIosCredentials
    brandsWithAndroidCredentials
    brandsNeedingIosMigration
    brandsNeedingAndroidMigration
    dailyMetrics {
      date
      deliveries
      taps
    }
  }
`;

const testMutation = graphql`
  mutation InternPushStatusCardTestMutation {
    testAzureNotificationHubConnection {
      azureApiConnected
      azureApiError
      notificationHubConnected
      notificationHubError
      ...InternPushStatusCard_status
    }
  }
`;

type Props = {
  status: InternPushStatusCard_status$key;
};

const InternPushStatusCard = ({ status: statusKey }: Props) => {
  const status = useFragment(statusFragment, statusKey);
  const snackbarContext = useContext(SnackbarContext);
  const { colorMode } = useColorMode();

  const chartColors = useMemo(
    () => ({
      grid: colorMode === "dark" ? "#374151" : "#e5e7eb",
      text: colorMode === "dark" ? "#9ca3af" : "#6b7280",
      tooltip: {
        bg: colorMode === "dark" ? "#1f2937" : "#ffffff",
        border: colorMode === "dark" ? "#374151" : "#e5e7eb",
      },
    }),
    [colorMode]
  );

  const chartData = useMemo(
    () =>
      status.dailyMetrics.map((m) => ({
        date: new Date(m.date).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
        Deliveries: m.deliveries,
        Taps: m.taps,
      })),
    [status.dailyMetrics]
  );

  const [testConnection, isTesting] =
    useMutation<InternPushStatusCardTestMutation>(testMutation);

  const handleTestConnection = useCallback(() => {
    testConnection({
      variables: {},
      onCompleted: (response) => {
        const result = response.testAzureNotificationHubConnection;
        const apiOk = result.azureApiConnected;
        const hubOk = result.notificationHubConnected;

        if (apiOk && hubOk) {
          snackbarContext?.openSnackbar(
            "All connections successful",
            "success"
          );
        } else if (apiOk && !hubOk) {
          snackbarContext?.openSnackbar(
            `API connected, but hub test failed: ${
              result.notificationHubError || "Unknown error"
            }`,
            "warning"
          );
        } else if (!apiOk && hubOk) {
          snackbarContext?.openSnackbar(
            `Hub connected, but API failed: ${
              result.azureApiError || "Unknown error"
            }`,
            "warning"
          );
        } else {
          snackbarContext?.openSnackbar(
            `Connection failed - API: ${
              result.azureApiError || "Error"
            }, Hub: ${result.notificationHubError || "Error"}`,
            "error"
          );
        }
      },
      onError: (error) => {
        snackbarContext?.openSnackbar(`Test failed: ${error.message}`, "error");
      },
    });
  }, [testConnection, snackbarContext]);

  const hubCoverage =
    status.totalActiveBrands > 0
      ? (status.brandsWithHub / status.totalActiveBrands) * 100
      : 0;
  const iosCoverage =
    status.totalActiveBrands > 0
      ? (status.brandsWithIosCredentials / status.totalActiveBrands) * 100
      : 0;
  const androidCoverage =
    status.totalActiveBrands > 0
      ? (status.brandsWithAndroidCredentials / status.totalActiveBrands) * 100
      : 0;

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        bgcolor: "var(--intern-bg-primary)",
        border: "1px solid var(--intern-border-default)",
        borderRadius: 2,
        height: "100%",
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <NotificationsActiveIcon
            sx={{ color: "var(--intern-accent)", fontSize: 20 }}
          />
          <Typography
            variant="subtitle2"
            sx={{
              color: "var(--intern-text-secondary)",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              fontSize: "0.6875rem",
            }}
          >
            Push Notifications
          </Typography>
        </Stack>
        <Button
          size="small"
          variant="outlined"
          startIcon={
            isTesting ? (
              <CircularProgress size={14} />
            ) : (
              <RefreshIcon sx={{ fontSize: 16 }} />
            )
          }
          onClick={handleTestConnection}
          disabled={isTesting}
          sx={{
            fontSize: "0.6875rem",
            py: 0.5,
            px: 1.5,
            minWidth: 0,
          }}
        >
          Test
        </Button>
      </Stack>

      {/* Status Indicators */}
      <Stack spacing={1} mb={2.5}>
        {/* Azure API Status */}
        <Stack direction="row" alignItems="center" spacing={1}>
          {status.azureApiConnected ? (
            <CheckCircleIcon sx={{ color: "#10b981", fontSize: 16 }} />
          ) : (
            <ErrorIcon sx={{ color: "#ef4444", fontSize: 16 }} />
          )}
          <Typography
            variant="body2"
            sx={{
              color: status.azureApiConnected ? "#10b981" : "#ef4444",
              fontWeight: 600,
              fontSize: "0.75rem",
            }}
          >
            API: {status.azureApiConnected ? "Connected" : "Error"}
          </Typography>
          {status.azureApiError && (
            <Tooltip title={status.azureApiError}>
              <ErrorIcon
                sx={{ color: "#ef4444", fontSize: 12, cursor: "help" }}
              />
            </Tooltip>
          )}
        </Stack>

        {/* Notification Hub Status */}
        <Stack direction="row" alignItems="center" spacing={1}>
          {status.notificationHubConnected ? (
            <CheckCircleIcon sx={{ color: "#10b981", fontSize: 16 }} />
          ) : (
            <ErrorIcon sx={{ color: "#ef4444", fontSize: 16 }} />
          )}
          <Typography
            variant="body2"
            sx={{
              color: status.notificationHubConnected ? "#10b981" : "#ef4444",
              fontWeight: 600,
              fontSize: "0.75rem",
            }}
          >
            Hub: {status.notificationHubConnected ? "Connected" : "Error"}
          </Typography>
          {status.notificationHubError && (
            <Tooltip title={status.notificationHubError}>
              <ErrorIcon
                sx={{ color: "#ef4444", fontSize: 12, cursor: "help" }}
              />
            </Tooltip>
          )}
        </Stack>
      </Stack>

      {/* Delivery Stats Section */}
      <Typography
        variant="caption"
        sx={{
          color: "var(--intern-text-tertiary)",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          fontSize: "0.625rem",
          display: "block",
          mb: 1.5,
        }}
      >
        Last 30 Days
      </Typography>

      <Stack
        direction="row"
        spacing={0}
        sx={{
          bgcolor: "var(--intern-bg-secondary)",
          borderRadius: 1,
          overflow: "hidden",
          mb: 2.5,
        }}
      >
        {/* Sent */}
        <Box
          sx={{
            flex: 1,
            p: 1.5,
            textAlign: "center",
            borderRight: "1px solid var(--intern-border-default)",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={0.5}
            mb={0.5}
          >
            <SendIcon
              sx={{ fontSize: 14, color: "var(--intern-text-tertiary)" }}
            />
            <Typography
              variant="caption"
              sx={{
                color: "var(--intern-text-tertiary)",
                fontSize: "0.625rem",
              }}
            >
              Campaigns Sent
            </Typography>
          </Stack>
          <Typography
            variant="h6"
            sx={{
              color: "var(--intern-text-primary)",
              fontWeight: 700,
              fontSize: "1rem",
            }}
          >
            {formatNumber(status.notificationsSent30d)}
          </Typography>
        </Box>

        {/* Delivered */}
        <Box
          sx={{
            flex: 1,
            p: 1.5,
            textAlign: "center",
            borderRight: "1px solid var(--intern-border-default)",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={0.5}
            mb={0.5}
          >
            <DevicesIcon
              sx={{ fontSize: 14, color: "var(--intern-text-tertiary)" }}
            />
            <Typography
              variant="caption"
              sx={{
                color: "var(--intern-text-tertiary)",
                fontSize: "0.625rem",
              }}
            >
              Delivered
            </Typography>
          </Stack>
          <Typography
            variant="h6"
            sx={{
              color: "var(--intern-text-primary)",
              fontWeight: 700,
              fontSize: "1rem",
            }}
          >
            {formatNumber(status.pushDeliveries30d)}
          </Typography>
        </Box>

        {/* Taps */}
        <Box
          sx={{
            flex: 1,
            p: 1.5,
            textAlign: "center",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={0.5}
            mb={0.5}
          >
            <TouchAppIcon
              sx={{ fontSize: 14, color: "var(--intern-text-tertiary)" }}
            />
            <Typography
              variant="caption"
              sx={{
                color: "var(--intern-text-tertiary)",
                fontSize: "0.625rem",
              }}
            >
              Taps
            </Typography>
          </Stack>
          <Typography
            variant="h6"
            sx={{
              color: "var(--intern-text-primary)",
              fontWeight: 700,
              fontSize: "1rem",
            }}
          >
            {formatNumber(status.pushTaps30d)}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "#3b82f6", fontSize: "0.625rem" }}
          >
            {(status.tapRate * 100).toFixed(1)}%
          </Typography>
        </Box>
      </Stack>

      <Divider sx={{ mb: 2 }} />

      {/* Configuration Section */}
      <Typography
        variant="caption"
        sx={{
          color: "var(--intern-text-tertiary)",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          fontSize: "0.625rem",
          display: "block",
          mb: 1.5,
        }}
      >
        Configuration
      </Typography>

      <Stack spacing={1.5}>
        {/* Hubs */}
        <Box>
          <Stack direction="row" justifyContent="space-between" mb={0.5}>
            <Typography
              variant="caption"
              sx={{
                color: "var(--intern-text-secondary)",
                fontSize: "0.6875rem",
              }}
            >
              Hubs
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "var(--intern-text-primary)",
                fontWeight: 600,
                fontSize: "0.6875rem",
              }}
            >
              {status.brandsWithHub}/{status.totalActiveBrands} (
              {hubCoverage.toFixed(0)}%)
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={hubCoverage}
            sx={{
              height: 4,
              borderRadius: 2,
              bgcolor: "var(--intern-bg-tertiary)",
              "& .MuiLinearProgress-bar": {
                borderRadius: 2,
                bgcolor: hubCoverage === 100 ? "#10b981" : "#6366f1",
              },
            }}
          />
        </Box>

        {/* iOS */}
        <Box>
          <Stack direction="row" justifyContent="space-between" mb={0.5}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <AppleIcon
                sx={{ fontSize: 14, color: "var(--intern-text-tertiary)" }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: "var(--intern-text-secondary)",
                  fontSize: "0.6875rem",
                }}
              >
                iOS
              </Typography>
            </Stack>
            <Typography
              variant="caption"
              sx={{
                color: "var(--intern-text-primary)",
                fontWeight: 600,
                fontSize: "0.6875rem",
              }}
            >
              {status.brandsWithIosCredentials}/{status.totalActiveBrands} (
              {iosCoverage.toFixed(0)}%)
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={iosCoverage}
            sx={{
              height: 4,
              borderRadius: 2,
              bgcolor: "var(--intern-bg-tertiary)",
              "& .MuiLinearProgress-bar": {
                borderRadius: 2,
                bgcolor: iosCoverage === 100 ? "#10b981" : "#6366f1",
              },
            }}
          />
        </Box>

        {/* Android */}
        <Box>
          <Stack direction="row" justifyContent="space-between" mb={0.5}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <AndroidIcon
                sx={{ fontSize: 14, color: "var(--intern-text-tertiary)" }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: "var(--intern-text-secondary)",
                  fontSize: "0.6875rem",
                }}
              >
                Android
              </Typography>
            </Stack>
            <Typography
              variant="caption"
              sx={{
                color: "var(--intern-text-primary)",
                fontWeight: 600,
                fontSize: "0.6875rem",
              }}
            >
              {status.brandsWithAndroidCredentials}/{status.totalActiveBrands} (
              {androidCoverage.toFixed(0)}%)
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={androidCoverage}
            sx={{
              height: 4,
              borderRadius: 2,
              bgcolor: "var(--intern-bg-tertiary)",
              "& .MuiLinearProgress-bar": {
                borderRadius: 2,
                bgcolor: androidCoverage === 100 ? "#10b981" : "#6366f1",
              },
            }}
          />
        </Box>
      </Stack>

      {/* Migration Warnings */}
      {(status.brandsNeedingIosMigration > 0 ||
        status.brandsNeedingAndroidMigration > 0) && (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 1.5 }} />
          <Stack spacing={0.75}>
            {status.brandsNeedingIosMigration > 0 && (
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <WarningAmberIcon sx={{ fontSize: 14, color: "#f59e0b" }} />
                <Typography
                  variant="caption"
                  sx={{ color: "#f59e0b", fontSize: "0.6875rem" }}
                >
                  {status.brandsNeedingIosMigration} brand
                  {status.brandsNeedingIosMigration > 1 ? "s" : ""} need iOS
                  token migration
                </Typography>
              </Stack>
            )}
            {status.brandsNeedingAndroidMigration > 0 && (
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <WarningAmberIcon sx={{ fontSize: 14, color: "#f59e0b" }} />
                <Typography
                  variant="caption"
                  sx={{ color: "#f59e0b", fontSize: "0.6875rem" }}
                >
                  {status.brandsNeedingAndroidMigration} brand
                  {status.brandsNeedingAndroidMigration > 1 ? "s" : ""} need
                  FCMv1 migration
                </Typography>
              </Stack>
            )}
          </Stack>
        </Box>
      )}

      {/* Devices Stats */}
      <Box sx={{ mt: 2 }}>
        <Divider sx={{ mb: 1.5 }} />
        <Stack direction="row" justifyContent="space-between">
          <Typography
            variant="caption"
            sx={{ color: "var(--intern-text-tertiary)", fontSize: "0.625rem" }}
          >
            Registered Devices
          </Typography>
          <Stack direction="row" spacing={1.5}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <AppleIcon
                sx={{ fontSize: 12, color: "var(--intern-text-tertiary)" }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: "var(--intern-text-secondary)",
                  fontSize: "0.625rem",
                }}
              >
                {formatNumber(status.iosDevices)}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <AndroidIcon
                sx={{ fontSize: 12, color: "var(--intern-text-tertiary)" }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: "var(--intern-text-secondary)",
                  fontSize: "0.625rem",
                }}
              >
                {formatNumber(status.androidDevices)}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Box>

      {/* Deliveries & Taps Chart */}
      <Box sx={{ mt: 2 }}>
        <Divider sx={{ mb: 1.5 }} />
        <Typography
          variant="caption"
          sx={{
            color: "var(--intern-text-tertiary)",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            fontSize: "0.625rem",
            display: "block",
            mb: 1,
          }}
        >
          Daily Deliveries &amp; Taps (14 Days)
        </Typography>
        <Box sx={{ height: 200 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <defs>
                <linearGradient
                  id="platformDeliveries"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="platformTaps" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={chartColors.grid}
                vertical={false}
              />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: chartColors.text }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tick={{ fontSize: 10, fill: chartColors.text }}
                tickLine={false}
                axisLine={false}
                width={35}
              />
              <RechartsTooltip
                contentStyle={{
                  backgroundColor: chartColors.tooltip.bg,
                  border: `1px solid ${chartColors.tooltip.border}`,
                  borderRadius: 8,
                  fontSize: "0.75rem",
                }}
              />
              <Legend
                iconType="circle"
                iconSize={8}
                wrapperStyle={{ fontSize: "0.6875rem" }}
              />
              <Area
                type="monotone"
                dataKey="Deliveries"
                stroke="#6366f1"
                fill="url(#platformDeliveries)"
                strokeWidth={2}
                isAnimationActive={false}
              />
              <Area
                type="monotone"
                dataKey="Taps"
                stroke="#10b981"
                fill="url(#platformTaps)"
                strokeWidth={2}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </Paper>
  );
};

export default InternPushStatusCard;
